/*
 * What the chat surface needs that only the running client can give it: the calls that send, edit,
 * delete and mark read, which go through the outbox and the socket the boot owns. Chat's binding
 * sets them once (`bind.ts`); a test sets the ones it is about. The composer is not one of them —
 * it is core's component and the surface renders it directly — and a call that is not bound has no
 * stand-in: an action whose call is missing is not offered at all.
 */

import type { AttachmentRef } from "../../core/drafts/types.ts"

export interface SendInput {
	conv: number
	markdown: string
	attachments: AttachmentRef[]
	/**
	 * Set by a thread composer: the message becomes a reply to that root and leaves the
	 * conversation's timeline (UX §4.1 R1). The boot turns it into the outbox target
	 * `c:<conv>:t:<root>`, which is also the draft key the thread's composer types under.
	 */
	threadRootId?: number
	/**
	 * "Also send to #channel" (X-A1 A4): the reply stands in the conversation's timeline as well as
	 * in its thread. It is read only beside `threadRootId`, and it travels on the outbox item rather
	 * than on its target, so the thread keeps one draft and one queue.
	 */
	alsoToChannel?: boolean
}

export interface ChatDeps {
	/** Hands the message to the outbox; it resolves when the send has been accepted for delivery. */
	send: (input: SendInput) => Promise<void>
	/**
	 * The four socket calls the surface offers but does not own: the edit flow with its
	 * `baseVersion` conflict, the author's own delete, and the two ends of the read cursor. An
	 * action whose call is not bound is not offered — a button that cannot do anything is worse
	 * than no button.
	 *
	 * An edit carries the files as well as the text: the server takes `attachments` as the whole
	 * new set (protocol.md §2.6), so a seam that took only markdown would unbind every file the
	 * message has — which is why the edit box used to reach past this seam and enqueue on the
	 * outbox itself (I42). One send path, one place that knows what an edit is.
	 */
	edit?:
		| ((input: {
				id: number
				markdown: string
				attachments: AttachmentRef[]
				baseVersion: number
		  }) => Promise<void>)
		| undefined
	remove?: ((input: { id: number }) => Promise<void>) | undefined
	markUnreadFrom?: ((input: { conv: number; seq: number }) => Promise<void>) | undefined
	/**
	 * Advances the read cursor to a seq the reader has seen. `auto` says which of the two callers
	 * this is, because the server treats them differently: an automatic write — the viewport rule —
	 * is refused while the reader is holding the conversation unread, and a deliberate one ("mark
	 * this conversation read") is what lifts that hold (`chat/unread/cursor.go`).
	 */
	markRead?: ((input: { conv: number; seq: number; auto: boolean }) => Promise<void>) | undefined
}

let deps: ChatDeps = {
	send: () => Promise.reject(new Error("chat send is not wired")),
}

/** Called once at boot with the calls behind the surface. */
export function setChatDeps(next: Partial<ChatDeps>): void {
	deps = { ...deps, ...next }
}

export function chatDeps(): ChatDeps {
	return deps
}
